import { Fragment, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import IconX from './Icon/IconX';
import { useTranslation } from 'react-i18next';
import { KeyboardShortcut, formatShortcut } from '../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsHelpProps {
    isOpen: boolean;
    onClose: () => void;
    shortcuts: KeyboardShortcut[];
}

const KeyboardShortcutsHelp = ({ isOpen, onClose, shortcuts }: KeyboardShortcutsHelpProps) => {
    const { t } = useTranslation();

    // Group shortcuts by category
    const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
        const category = shortcut.description.includes('Navigate') ? 'Navigation' :
            shortcut.description.includes('Add') || shortcut.description.includes('Create') ? 'Actions' :
                shortcut.description.includes('Search') ? 'Search' :
                    'General';

        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(shortcut);
        return acc;
    }, {} as Record<string, KeyboardShortcut[]>);

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" open={isOpen} onClose={onClose} className="relative z-50">
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-[black]/60" />
                </TransitionChild>
                <div className="fixed inset-0 z-[999] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="panel border-0 p-0 rounded-lg overflow-hidden w-full max-w-2xl text-black dark:text-white-dark">
                                <button
                                    onClick={onClose}
                                    type="button"
                                    className="absolute top-4 ltr:right-4 rtl:left-4 text-white-dark hover:text-dark z-10"
                                >
                                    <IconX />
                                </button>
                                <div className="text-lg font-medium bg-[#fbfbfb] dark:bg-[#121c2c] ltr:pl-5 rtl:pr-5 py-3 ltr:pr-[50px] rtl:pl-[50px]">
                                    Keyboard Shortcuts
                                </div>
                                <div className="p-5 max-h-[70vh] overflow-y-auto">
                                    {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
                                        <div key={category} className="mb-6 last:mb-0">
                                            <h3 className="text-md font-semibold mb-3 text-primary">{category}</h3>
                                            <div className="space-y-2">
                                                {categoryShortcuts.map((shortcut, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex items-center justify-between py-2 px-3 rounded bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    >
                                                        <span className="text-sm">{shortcut.description}</span>
                                                        <kbd className="px-3 py-1 text-xs font-semibold text-gray-800 bg-gray-200 border border-gray-300 rounded dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                                                            {formatShortcut(shortcut)}
                                                        </kbd>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-end items-center p-5 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        onClick={onClose}
                                        type="button"
                                        className="btn btn-primary"
                                    >
                                        Close
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default KeyboardShortcutsHelp;
